import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../Layout/DashboardLayout';
import LoadingSpinner from '../Common/LoadingSpinner';
import './Templates.css';
import { requestHelpers } from '../../services/api';
import './Templates.css';

const UTILITY_LIBRARY = [
  {
    id: 'otp',
    title: 'One-Time Password',
    subcategory: 'Account or product protection',
    icon: 'fa-shield-alt',
    description: 'Send verification codes or security alerts.',
    sample: 'Your verification code is {{1}}.',
  },
  {
    id: 'receipt',
    title: 'Order Receipt',
    subcategory: 'Order management',
    icon: 'fa-receipt',
    description: 'Confirm order details and payment.',
    sample: 'Your payment of {{1}} for order {{2}} was successful.',
  },
  {
    id: 'shipping',
    title: 'Shipping Update',
    subcategory: 'Order management',
    icon: 'fa-truck',
    description: 'Send order tracking and delivery updates.',
    sample: 'Your order #{{1}} has been shipped. Tracking: {{2}}',
  },
  {
    id: 'delivery',
    title: 'Delivery Confirmation',
    subcategory: 'Order management',
    icon: 'fa-box-check',
    description: 'Notify users when order is delivered.',
    sample: 'Your order {{1}} has been delivered. Enjoy!',
  },
  {
    id: 'refund_update',
    title: 'Refund Status',
    subcategory: 'Payments',
    icon: 'fa-money-bill-wave',
    description: 'Update user on refund processing.',
    sample: 'We have initiated a refund of {{1}} for order {{2}}. It will reflect in your account in 3-5 days.',
  },
  {
    id: 'waitlist_alert',
    title: 'Back in Stock',
    subcategory: 'Other',
    icon: 'fa-bell',
    description: 'Alert user that a product is available.',
    sample: 'Good news! {{1}} is back in stock. Order now before it sells out again!',
  },
  {
    id: 'account_update',
    title: 'Account Update',
    subcategory: 'Account updates',
    icon: 'fa-user-check',
    description: 'Notify users about account status or security.',
    sample: 'Your account {{1}} has been successfully updated.',
  },
  {
    id: 'payment_reminder',
    title: 'Payment Reminder',
    subcategory: 'Payments',
    icon: 'fa-credit-card',
    description: 'Remind users about upcoming or overdue payments.',
    sample: 'Your payment for {{1}} is due on {{2}}.',
  },
  {
    id: 'appointment_confirm',
    title: 'Appointment Confirm',
    subcategory: 'Event reminder',
    icon: 'fa-calendar-check',
    description: 'Send confirmation for bookings.',
    sample: 'Your appointment with {{1}} is confirmed for {{2}}.',
  },
  {
    id: 'customer_feedback',
    title: 'Customer Feedback',
    subcategory: 'Customer feedback',
    icon: 'fa-comment-alt-edit',
    description: 'Invite users to share their experience.',
    sample: 'Hi {{1}}, how was your recent experience with {{2}}?',
  }
];

const UTILITY_SUBCATEGORIES = [
  'All Utilities',
  'Account or product protection',
  'Account updates',
  'Order management',
  'Payments',
  'Customer feedback',
  'Event reminder',
  'Waitlist updates',
  'Public safety',
  'Legal/regulatory compliance'
];

const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [filters, setFilters] = useState({
    category: 'all',
    search: ''
  });
  const { user } = useAuth();

  const [newTemplate, setNewTemplate] = useState({
    name: '',
    content: '',
    category: 'UTILITY',
    subcategory: '',
    variables: [],
    buttons: [],
    type: 'text'
  });

  const [libraryCategory, setLibraryCategory] = useState('UTILITY');
  const [utilitySubcategory, setUtilitySubcategory] = useState('All Utilities');
  const [selectedLibraryId, setSelectedLibraryId] = useState(null);

  const templateCategories = [
    { value: 'MARKETING', label: 'Marketing' },
    { value: 'UTILITY', label: 'Utility' },
    { value: 'AUTHENTICATION', label: 'Authentication' }
  ];

  const availableVariables = [
    { value: 'name', label: '{{name}}', description: 'Contact name' },
    { value: 'email', label: '{{email}}', description: 'Contact email' },
    { value: 'phone', label: '{{phone}}', description: 'Contact phone' },
    { value: 'company', label: '{{company}}', description: 'Company name' },
    { value: 'custom1', label: '{{custom1}}', description: 'Custom field 1' },
    { value: 'custom2', label: '{{custom2}}', description: 'Custom field 2' },
    { value: 'custom3', label: '{{custom3}}', description: 'Custom field 3' }
  ];

  const [searchTerm, setSearchTerm] = useState('');

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchTerm }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (user?.whatsappConnected) {
      handleSyncStatus(false); // Silent sync once on mount or connection change
    }
  }, [user?.whatsappConnected]);

  useEffect(() => {
    fetchTemplates();
  }, [filters.category, filters.search]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await requestHelpers.get('/templates', {
        params: filters
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      toast.error('Failed to fetch templates');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const handleSyncStatus = async (showToast = true) => {
    try {
      if (showToast) toast.info('Syncing status with Meta...');
      const response = await requestHelpers.get('/templates/sync');
      if (response.success) {
        if (showToast) toast.success('Status synchronized successfully');
        fetchTemplates();
      }
    } catch (error) {
      if (showToast) toast.error('Failed to sync status');
      console.error('Sync error:', error);
    }
  };

  // const handleCreateTemplate = async (e) => {
  //   e.preventDefault();
  //   try {
  //     // Extract variables from content
  //     const variableMatches = newTemplate.content.match(/\{\{(\w+)\}\}/g) || [];
  //     const extractedVariables = [...new Set(variableMatches.map(match => match.slice(2, -2)))];

  //     const templateData = {
  //       ...newTemplate,
  //       variables: extractedVariables
  //     };

  //     // Mock API call
  //     toast.success('Template created successfully!');
  //     setShowCreateModal(false);
  //     setNewTemplate({
  //       name: '',
  //       content: '',
  //       category: 'general',
  //       variables: []
  //     });
  //     fetchTemplates();
  //   } catch (error) {
  //     toast.error('Failed to create template');
  //   }
  // };


  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      // 1️⃣ Extract variables from content
      const variableMatches = newTemplate.content.match(/\{\{(\w+)\}\}/g) || [];
      const extractedVariables = [...new Set(variableMatches.map(match => match.slice(2, -2)))];

      // 2️⃣ Validate buttons
      const buttons = newTemplate.buttons || [];
      
      // 3. Meta Restriction: No leading/trailing variables (punctuation doesn't count as text)
      const bodyText = newTemplate.content;
      if (/^\{\{\d+\}\}/.test(bodyText.trim().replace(/[.,!?;:]/g, ''))) {
        toast.error("Meta API restriction: Variables cannot be at the very START of your template (punctuation doesn't count). Please add words before it.");
        return;
      }
      
      if (/\{\{\d+\}\}$/.test(bodyText.trim().replace(/[.,!?;:]/g, ''))) {
        toast.error("Meta API restriction: Variables cannot be at the very END of your template (punctuation doesn't count). Please add words after it.");
        return;
      }

      // 4. Mixing buttons validation
      if (buttons.length > 3) {
        toast.error('Maximum 3 buttons allowed per template.');
        return;
      }

      const urlButtons = buttons.filter(b => b.type === 'url');
      if (urlButtons.length > 1) {
        toast.error('Only 1 URL button is allowed.');
        return;
      }

      const phoneButtons = buttons.filter(b => b.type === 'phone');
      if (phoneButtons.length > 1) {
        toast.error('Only 1 Phone button is allowed.');
        return;
      }

      // 3️⃣ Media Handling: Upload header file if present
      let headerFormat = 'TEXT';
      let headerMediaUrl = '';

      if (['image', 'video', 'document'].includes(newTemplate.type) && newTemplate.headerFile) {
        // 🔥 IMMEDIATE VALIDATION: Check Meta's size restrictions before uploading
        const file = newTemplate.headerFile;
        const type = newTemplate.type;
        let limit = 0;
        let limitLabel = '';

        if (type === 'image') {
          limit = 5 * 1024 * 1024;
          limitLabel = '5MB';
        } else if (type === 'video') {
          limit = 16 * 1024 * 1024;
          limitLabel = '16MB';
        } else if (type === 'document') {
          limit = 100 * 1024 * 1024;
          limitLabel = '100MB';
        }

        if (file.size > limit) {
          toast.error(`Header ${type} exceeds Meta's size limit of ${limitLabel}. Please compress the file or choose a smaller one.`);
          return;
        }

        toast.info(`Uploading ${newTemplate.type} header...`);
        const formData = new FormData();
        formData.append('file', newTemplate.headerFile);
        formData.append('fileName', `${newTemplate.name}_header`);
        formData.append('purpose', 'template'); // Flag for Resumable Upload Handle

        const mediaResponse = await requestHelpers.post('/media/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (mediaResponse.success) {
          headerFormat = newTemplate.type.toUpperCase();
          headerMediaUrl = mediaResponse.data.headerHandle || mediaResponse.data.mediaId; // Use Handle for template registration
        } else {
          throw new Error('Failed to upload media header');
        }
      } else if (newTemplate.header) {
        headerFormat = 'TEXT';
      }

      // 4️⃣ Prepare final payload
      const templateData = {
        ...newTemplate,
        headerFormat,
        headerMediaUrl,
        variables: extractedVariables,
        buttons: buttons.map(btn => ({
          type: btn.type,
          text: btn.text,
          ...(btn.type === 'url' && { url: btn.url }),
          ...(btn.type === 'phone' && { phone: btn.phone })
        }))
      };

      // 4️⃣ Call API (replace this with actual API call)
      await requestHelpers.post('/templates', templateData);

      toast.success('Template created successfully!');
      setShowCreateModal(false);

      // 5️⃣ Reset form
      setNewTemplate({
        name: '',
        content: '',
        category: 'general',
        variables: [],
        buttons: []
      });

      fetchTemplates(); // Refresh the template list
    } catch (error) {
      console.error(error);
      toast.error('Failed to create template');
    }
  };


  const handleDeleteTemplate = async (templateId) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        setTemplates(prev => prev.filter(template => template.id !== templateId));
        toast.success('Template deleted successfully');
      } catch (error) {
        toast.error('Failed to delete template');
      }
    }
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      const duplicatedTemplate = {
        ...template,
        id: Date.now(),
        name: `${template.name} (Copy)`,
        usageCount: 0,
        createdAt: new Date().toISOString()
      };
      setTemplates(prev => [duplicatedTemplate, ...prev]);
      toast.success('Template duplicated successfully');
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
  };

  const handleLibrarySelect = (item) => {
    setSelectedLibraryId(item.id);
    setNewTemplate({
      ...newTemplate,
      name: item.id === 'otp' ? 'verification_code' : item.id,
      content: item.sample,
      category: 'UTILITY',
      subcategory: item.subcategory,
      type: 'text'
    });
    // Scroll content area into view if needed
    toast.info(`Populated fields for ${item.title}`);
  };

  const insertVariable = (variable) => {
    const textarea = document.querySelector('textarea[name="content"]');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = newTemplate.content;
      const before = text.substring(0, start);
      const after = text.substring(end, text.length);
      const newContent = before + `{{${variable}}}` + after;

      setNewTemplate({ ...newTemplate, content: newContent });

      // Set cursor position after the inserted variable
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length + 4, start + variable.length + 4);
      }, 0);
    }
  };

  const previewTemplate = (template) => {
    let preview = template.content;

    // Replace variables with sample data
    const sampleData = {
      name: 'John Doe',
      email: 'john@example.com',
      phone: '+1234567890',
      company: 'Example Corp',
      custom1: 'Sample Value 1',
      custom2: 'Sample Value 2',
      custom3: 'Sample Value 3'
    };

    template.variables.forEach(variable => {
      const regex = new RegExp(`\\{\\{${variable}\\}\\}`, 'g');
      preview = preview.replace(regex, sampleData[variable] || `[${variable}]`);
    });

    return preview;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryBadge = (category) => {
    const badges = {
      MARKETING: 'badge-success',
      UTILITY: 'badge-info',
      AUTHENTICATION: 'badge-warning'
    };
    return badges[category] || 'badge-secondary';
  };

  if (initialLoading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <LoadingSpinner />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="templates-page">
        <div className="page-header">
          <div className="header-content">
            <h1>Templates</h1>
            <p>Create and manage your message templates</p>
            {user?.whatsappNumber && (
              <div className="header-number-badge mt-2" style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' }}>
                <i className="fab fa-whatsapp"></i>
                <span>Linked to: {user.whatsappNumber}</span>
              </div>
            )}
            {!user?.whatsappConnected && (
              <div className="connection-warning-banner">
                <i className="fas fa-exclamation-triangle"></i>
                <span>WhatsApp is not connected. Please <a href="/connect-whatsapp">connect your account</a> to sync templates.</span>
              </div>
            )}
          </div>
          <div className="header-actions">
            <button
              className="btn btn-secondary"
              onClick={() => handleSyncStatus(true)}
              style={{ marginRight: '10px' }}
              title="Fetch all templates from Meta"
            >
              <i className="fas fa-sync"></i>
              Sync from Meta
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <i className="fas fa-plus"></i>
              Create Template
            </button>
          </div>
        </div>

        <div className="templates-filters">
          <div className="filters-row">
            <div className="search-box">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="category-filter"
            >
              <option value="all">All Categories</option>
              {templateCategories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="templates-grid">
          {templates.map(template => (
            <div key={template.id} className="template-card">
              <div className="template-header">
                <div className="template-title">
                  <h3>{template.name}</h3>
                  <span className={`category-badge ${getCategoryBadge(template.category)}`}>
                    <i className="fas fa-tag"></i>
                    {template.category}
                  </span>
                </div>
                <div className="template-actions">
                  <button
                    className="action-btn"
                    onClick={() => handleDuplicateTemplate(template)}
                    title="Duplicate"
                  >
                    <i className="fas fa-copy"></i>
                  </button>
                  <button
                    className="action-btn delete-btn"
                    onClick={() => handleDeleteTemplate(template.id)}
                    title="Delete"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>

              <div className="template-content">
                <p>{template.content}</p>
              </div>

              {template.variables.length > 0 && (
                <div className="template-variables">
                  <div className="variables-label">Dynamic Variables</div>
                  <div className="variables-list">
                    {template.variables.map(variable => (
                      <span key={variable} className="variable-tag">
                        {`{{${variable}}}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="template-footer">
                <div className="template-stats">
                  <span className="usage-count">
                    <i className="fas fa-chart-bar"></i>
                    {template.usageCount} executions
                  </span>
                  <span className="last-updated">
                    Updated {formatDate(template.updatedAt)}
                  </span>
                </div>
                <div className="template-controls">
                </div>
              </div>
            </div>
          ))}
        </div>

        {templates.length === 0 && (
          <div className="empty-state">
            <i className="fas fa-file-alt"></i>
            <h3>No templates found</h3>
            <p>Create your first template to streamline your messaging</p>
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              Create Template
            </button>
          </div>
        )}

        {/* Create Template Modal */}
        {/* {showCreateModal && (
          <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div className="modal-content template-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Create New Template</h2>
                <button 
                  className="modal-close"
                  onClick={() => setShowCreateModal(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="template-form-container">
                <form onSubmit={handleCreateTemplate} className="template-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Template Name *</label>
                      <input
                        type="text"
                        value={newTemplate.name}
                        onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                        placeholder="Enter template name"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Category</label>
                      <select
                        value={newTemplate.category}
                        onChange={(e) => setNewTemplate({...newTemplate, category: e.target.value})}
                      >
                        {templateCategories.map(category => (
                          <option key={category.value} value={category.value}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="template-editor">
                    <div className="editor-section">
                      <div className="form-group">
                        <label>Message Content *</label>
                        <textarea
                          name="content"
                          value={newTemplate.content}
                          onChange={(e) => setNewTemplate({...newTemplate, content: e.target.value})}
                          placeholder="Enter your message content..."
                          rows="6"
                          maxLength="1600"
                          required
                        />
                        <small>{newTemplate.content.length}/1600 characters</small>
                      </div>
                    </div>

                    <div className="variables-section">
                      <h4>Available Variables</h4>
                      <div className="variables-grid">
                        {availableVariables.map(variable => (
                          <div 
                            key={variable.value} 
                            className="variable-item"
                            onClick={() => insertVariable(variable.value)}
                          >
                            <div className="variable-code">{variable.label}</div>
                            <div className="variable-desc">{variable.description}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {newTemplate.content && (
                    <div className="template-preview">
                      <h4>Preview</h4>
                      <div className="preview-content">
                        {previewTemplate({ 
                          content: newTemplate.content, 
                          variables: newTemplate.content.match(/\{\{(\w+)\}\}/g)?.map(match => match.slice(2, -2)) || []
                        })}
                      </div>
                    </div>
                  )}

                  <div className="form-actions">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Create Template
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )} */}

        {/* Preview Modal */}
        {/* {selectedTemplate && (
          <div className="modal-overlay" onClick={() => setSelectedTemplate(null)}>
            <div className="modal-content preview-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Template Preview</h2>
                <button 
                  className="modal-close"
                  onClick={() => setSelectedTemplate(null)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="preview-container">
                <div className="template-info">
                  <h3>{selectedTemplate.name}</h3>
                  <div className="template-meta">
                    <span className={`category-badge ${getCategoryBadge(selectedTemplate.category)}`}>
                      {selectedTemplate.category}
                    </span>
                    <span className="usage-info">Used {selectedTemplate.usageCount} times</span>
                  </div>
                </div>

                <div className="preview-sections">
                  <div className="original-section">
                    <h4>Original Template</h4>
                    <div className="code-block">
                      {selectedTemplate.content}
                    </div>
                  </div>

                  <div className="rendered-section">
                    <h4>Preview with Sample Data</h4>
                    <div className="preview-message">
                      {previewTemplate(selectedTemplate)}
                    </div>
                  </div>

                  {selectedTemplate.variables.length > 0 && (
                    <div className="variables-section">
                      <h4>Variables Used</h4>
                      <div className="variables-list">
                        {selectedTemplate.variables.map(variable => (
                          <span key={variable} className="variable-tag">
                            {`{{${variable}}}`}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="preview-actions">
                  <button className="btn btn-secondary" onClick={() => setSelectedTemplate(null)}>
                    Close
                  </button>
                  <button className="btn btn-primary">
                    Use This Template
                  </button>
                </div>
              </div>
            </div>
          </div>
        )} */}



        {/* Create Template Modal */}
        {showCreateModal && (
          <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div
              className="modal-content template-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Create New Template</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowCreateModal(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="template-form-container">
                <form onSubmit={handleCreateTemplate} className="template-form">
                  {/* Template Library Selection */}
                  <div className="template-library-section mb-4" style={{ background: 'rgba(59, 130, 246, 0.05)', padding: '20px', borderRadius: '16px', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <h3 style={{ fontSize: '16px', fontWeight: '800', margin: 0, color: '#1e40af' }}>
                          <i className="fas fa-book-open mr-2"></i> Template Library
                        </h3>
                        <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>Select a starting point to auto-populate fields</p>
                      </div>
                      <div className="library-tabs" style={{ display: 'flex', gap: '8px' }}>
                        {['UTILITY', 'MARKETING', 'AUTHENTICATION'].map(cat => (
                          <button
                            key={cat}
                            type="button"
                            className={`tab-btn ${libraryCategory === cat ? 'active' : ''}`}
                            onClick={() => setLibraryCategory(cat)}
                            style={{
                              padding: '4px 12px',
                              borderRadius: '20px',
                              border: '1px solid var(--color-border)',
                              background: libraryCategory === cat ? 'var(--color-primary)' : 'white',
                              color: libraryCategory === cat ? 'white' : 'var(--color-text)',
                              fontSize: '11px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            {cat}
                          </button>
                        ))}
                      </div>
                    </div>

                    {libraryCategory === 'UTILITY' ? (
                      <>
                        <div className="subcategory-scroll mb-3" style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px' }}>
                          {UTILITY_SUBCATEGORIES.map(sub => (
                            <button
                              key={sub}
                              type="button"
                              className={`sub-pill ${utilitySubcategory === sub ? 'active' : ''}`}
                              onClick={() => setUtilitySubcategory(sub)}
                              style={{
                                padding: '4px 10px',
                                borderRadius: '15px',
                                border: '1px solid rgba(59,130,246,0.2)',
                                background: utilitySubcategory === sub ? 'rgba(59,130,246,0.1)' : 'white',
                                color: utilitySubcategory === sub ? '#1e40af' : '#6b7280',
                                fontSize: '10px',
                                fontWeight: '600',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer'
                              }}
                            >
                              {sub}
                            </button>
                          ))}
                        </div>

                        <div className="library-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                          {UTILITY_LIBRARY
                            .filter(item => utilitySubcategory === 'All Utilities' || item.subcategory === utilitySubcategory)
                            .map(item => (
                              <div
                                key={item.id}
                                className={`library-item-card ${selectedLibraryId === item.id ? 'active' : ''}`}
                                onClick={() => handleLibrarySelect(item)}
                                style={{
                                  padding: '12px',
                                  borderRadius: '12px',
                                  border: `1px solid ${selectedLibraryId === item.id ? 'var(--color-primary)' : 'rgba(0,0,0,0.05)'}`,
                                  background: selectedLibraryId === item.id ? 'rgba(59,130,246,0.02)' : 'white',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  boxShadow: selectedLibraryId === item.id ? '0 4px 12px rgba(59,130,246,0.1)' : 'none'
                                }}
                              >
                                <div className="d-flex align-items-center gap-2 mb-2">
                                  <i className={`fas ${item.icon}`} style={{ color: 'var(--color-primary)', fontSize: '14px' }}></i>
                                  <span style={{ fontSize: '13px', fontWeight: '700' }}>{item.title}</span>
                                </div>
                                <p style={{ fontSize: '11px', color: '#6b7280', margin: 0, lineHeight: '1.4' }}>{item.description}</p>
                              </div>
                            ))}
                        </div>
                      </>
                    ) : (
                      <div className="p-4 text-center" style={{ color: '#9ca3af', fontSize: '12px', background: 'white', borderRadius: '12px' }}>
                        <i className="fas fa-tools mb-2" style={{ fontSize: '20px' }}></i>
                        <p>Marketing and Authentication presets coming soon...</p>
                      </div>
                    )}
                  </div>

                  {/* Template Name + Category */}
                  <div className="form-row">
                    <div className="form-group">
                      <label>Template Name *</label>
                      <input
                        type="text"
                        value={newTemplate.name}
                        onChange={(e) =>
                          setNewTemplate({ ...newTemplate, name: e.target.value })
                        }
                        placeholder="Enter template name"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Category *</label>
                      <select
                        value={newTemplate.category}
                        onChange={(e) =>
                          setNewTemplate({ ...newTemplate, category: e.target.value, subcategory: e.target.value === 'UTILITY' ? utilitySubcategory : '' })
                        }
                      >
                        {templateCategories.map((category) => (
                          <option key={category.value} value={category.value}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Subcategory */}
                  {newTemplate.category === 'UTILITY' && (
                    <div className="form-group">
                      <label>Subcategory *</label>
                      <select
                        value={newTemplate.subcategory || utilitySubcategory}
                        onChange={(e) =>
                          setNewTemplate({ ...newTemplate, subcategory: e.target.value })
                        }
                        required
                      >
                         {UTILITY_SUBCATEGORIES.filter(s => s !== 'All Utilities').map(sub => (
                           <option key={sub} value={sub}>{sub}</option>
                         ))}
                      </select>
                    </div>
                  )}

                  {/* Message Type */}
                  <div className="form-group">
                    <label>Message Type *</label>
                    <select
                      value={newTemplate.type || "text"}
                      onChange={(e) =>
                        setNewTemplate({ ...newTemplate, type: e.target.value })
                      }
                    >
                      <option value="text">Text</option>
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                      <option value="document">Document</option>
                      {/* <option value="buttons">Buttons</option> */}
                    </select>
                  </div>

                  {/* Header (optional text or media) */}
                  <div className="form-group">
                    <label>Header (Optional)</label>
                    {["image", "video", "document"].includes(newTemplate.type) ? (
                      <input
                        type="file"
                        accept={
                          newTemplate.type === "image"
                            ? "image/*"
                            : newTemplate.type === "video"
                              ? "video/*"
                              : ".pdf,.doc,.docx"
                        }
                        onChange={(e) =>
                          setNewTemplate({
                            ...newTemplate,
                            headerFile: e.target.files[0],
                          })
                        }
                      />
                    ) : (
                      <input
                        type="text"
                        value={newTemplate.header || ""}
                        onChange={(e) =>
                          setNewTemplate({ ...newTemplate, header: e.target.value })
                        }
                        placeholder="Header text (optional)"
                      />
                    )}
                  </div>

                  {/* Message Content */}
                  <div className="template-editor">
                    <div className="editor-section">
                      <div className="form-group">
                        <label>Message Content *</label>
                        <textarea
                          name="content"
                          value={newTemplate.content}
                          onChange={(e) =>
                            setNewTemplate({
                              ...newTemplate,
                              content: e.target.value,
                            })
                          }
                          placeholder="Enter your message content..."
                          rows="6"
                          maxLength="1600"
                          required
                        />
                        <small>{newTemplate.content.length}/1600 characters</small>
                      </div>
                    </div>

                    {/* Variable Insert */}
                    <div className="variables-section">
                      <h4>Available Variables</h4>
                      <div className="variables-grid">
                        {availableVariables.map((variable) => (
                          <div
                            key={variable.value}
                            className="variable-item"
                            onClick={() => insertVariable(variable.value)}
                          >
                            <div className="variable-code">{variable.label}</div>
                            <div className="variable-desc">{variable.description}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Footer (optional) */}
                  <div className="form-group">
                    <label>Footer (Optional)</label>
                    <input
                      type="text"
                      value={newTemplate.footer || ""}
                      onChange={(e) =>
                        setNewTemplate({ ...newTemplate, footer: e.target.value })
                      }
                      placeholder="Disclaimer or note"
                    />
                  </div>

                  {/* Buttons (works for all template types) */}
                  <div className="form-group">
                    <label>Buttons</label>
                    <div className="buttons-list">
                      {(newTemplate.buttons || []).map((btn, idx) => (
                        <div key={idx} className="button-item">
                          <select
                            value={btn.type}
                            onChange={(e) => {
                              const updated = [...newTemplate.buttons];
                              updated[idx].type = e.target.value;
                              setNewTemplate({ ...newTemplate, buttons: updated });
                            }}
                          >
                            <option value="quick_reply">Quick Reply</option>
                            <option value="url">Call to Action: Open URL</option>
                            <option value="phone">Call to Action: Phone Number</option>
                          </select>

                          {/* Button Text */}
                          <input
                            type="text"
                            value={btn.text}
                            onChange={(e) => {
                              const updated = [...newTemplate.buttons];
                              updated[idx].text = e.target.value;
                              setNewTemplate({ ...newTemplate, buttons: updated });
                            }}
                            placeholder="Button text"
                            required
                          />

                          {/* Extra fields based on type */}
                          {btn.type === "url" && (
                            <div className="url-field-wrapper" style={{ flex: 1 }}>
                              <input
                                type="url"
                                value={btn.url || ""}
                                onChange={(e) => {
                                  const updated = [...newTemplate.buttons];
                                  updated[idx].url = e.target.value;
                                  setNewTemplate({ ...newTemplate, buttons: updated });
                                }}
                                placeholder="https://example.com"
                                style={{ width: '100%' }}
                              />
                              <div style={{ fontSize: '11px', color: '#10b981', marginTop: '4px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <i className="fas fa-shield-check"></i>
                                <span>Zero-Config Tracking Enabled</span>
                              </div>
                            </div>
                          )}

                          {btn.type === "phone" && (
                            <input
                              type="tel"
                              value={btn.phone || ""}
                              onChange={(e) => {
                                const updated = [...newTemplate.buttons];
                                updated[idx].phone = e.target.value;
                                setNewTemplate({ ...newTemplate, buttons: updated });
                              }}
                              placeholder="+91 9876543210"
                            />
                          )}

                          {/* Remove button */}
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => {
                              const updated = newTemplate.buttons.filter((_, i) => i !== idx);
                              setNewTemplate({ ...newTemplate, buttons: updated });
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          const buttons = newTemplate.buttons || [];

                          const quickReplyCount = buttons.filter(b => b.type === "quick_reply").length;
                          const urlCount = buttons.filter(b => b.type === "url").length;
                          const phoneCount = buttons.filter(b => b.type === "phone").length;

                          if (buttons.length >= 3) {
                            toast.warning("You can add a maximum of 3 buttons per template.");
                            return;
                          }

                          // Example: only 1 URL and 1 Phone allowed
                          if (quickReplyCount + urlCount + phoneCount >= 3) {
                            toast.warning("Maximum buttons reached according to WhatsApp rules.");
                            return;
                          }

                          setNewTemplate({
                            ...newTemplate,
                            buttons: [
                              ...buttons,
                              { type: "quick_reply", text: "" }
                            ],
                          });
                        }}
                      >
                        + Add Button
                      </button>

                    </div>
                  </div>


                  {/* Live Preview */}
                  {(newTemplate.content || newTemplate.header || newTemplate.footer) && (
                    <div className="template-preview">
                      <h4>Preview</h4>
                      <div className="preview-content">
                        {newTemplate.header && (
                          <div className="preview-header">
                            {newTemplate.headerFile ? (
                              <span>[Media Uploaded]</span>
                            ) : (
                              <strong>{newTemplate.header}</strong>
                            )}
                          </div>
                        )}

                        <div className="preview-body">
                          {previewTemplate({
                            content: newTemplate.content,
                            variables:
                              newTemplate.content
                                .match(/\{\{(\w+)\}\}/g)
                                ?.map((m) => m.slice(2, -2)) || [],
                          })}
                        </div>

                        {newTemplate.footer && (
                          <div className="preview-footer">{newTemplate.footer}</div>
                        )}

                        {newTemplate.buttons?.length > 0 && (
                          <div className="preview-buttons">
                            {newTemplate.buttons.map((btn, idx) => (
                              <button key={idx} className="btn btn-light btn-sm">
                                {btn.text || "Button"}
                                {btn.type === "url" && " 🔗"}
                                {btn.type === "phone" && " 📞"}
                              </button>
                            ))}
                          </div>
                        )}

                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowCreateModal(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Create Template
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
};

export default Templates;